/* @HEADER
 * @version 1.0.1674 | 2026-09-20
 * @purpose Register the PWA service worker in production builds only, and surface a "new version available" prompt (ZVX-DEV-3675) instead of silently sitting on a stale bundle. Guarded by import.meta.env.PROD so dev mode is unaffected and Vite HMR continues to work normally. Drives the decision from the web bundle's own version.json (ZVX-DEV-4423), not the API's /health, and suppresses a repeat prompt for an update already tried from this exact version.
 * @sidecar registerSW.ts.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-3713, ZVX-DEV-3717, ZVX-DEV-4423, ZVX-DEV-6171, ZVX-DEV-6219, ZVX-DEV-6227

import {
  shouldPromptForUpdate,
  SW_UPDATE_AVAILABLE_EVENT,
  SW_UPDATE_RESOLVED_EVENT,
  SW_UPDATE_STALE_EVENT,
  readUpdateAttempt,
  clearUpdateAttempt,
  isRepeatedFailedAttempt,
  SW_UPDATE_AUTO_APPLY_EVENT,
} from './pwa/swUpdate';
import {
  fetchWebVersionWithFallback,
  isNewerVersionAvailable,
  armHeartbeatPolling,
} from './pwa/versionHeartbeat';
import appVersion from 'virtual:app-version';
import {
  createHiddenTracker,
  decideIdleApply,
  hasActiveEdit,
  shouldDispatchOffer,
} from './pwa/idleApply';
import { reportWebOriginTrouble } from './lib/clientVersion';
import { offlineSkippingFetch } from './lib/offlineSkippingFetch.mjs';
import { resolveApiBase } from './shared/config/apiConfig';

/**
 * Registers /sw.js in production builds only.
 *
 * Guard rationale:
 *   In development, a registered SW intercepts Vite HMR fetch events and can
 *   serve stale cached responses instead of fresh module files — breaking hot
 *   reload and making the dev loop unreliable on localhost:3000.
 *   import.meta.env.PROD is false in `vite dev` and true in `vite build` output,
 *   so this guard is both correct and build-time-eliminated in dev bundles.
 *
 * SW scope: '/' — the entire origin.
 * updateViaCache: 'none' — browser always re-fetches sw.js itself (bypasses HTTP cache).
 *   Critical: ensures users always receive the latest SW version without manual cache clear.
 *
 * ZVX-DEV-3675 — update notification, two signals feeding one prompt:
 *
 *   1. SW-install path: `sw.js`'s install handler no longer calls
 *      skipWaiting() unconditionally, so an updated worker now genuinely
 *      sits in `registration.waiting` until asked to take over. This
 *      function watches `updatefound` for that worker reaching `installed`;
 *      when {@link shouldPromptForUpdate} says this is a real update (not
 *      the page's first-ever SW install), it dispatches
 *      {@link SW_UPDATE_AVAILABLE_EVENT}.
 *   2. Version-heartbeat path (fallback/fast-path): polls the web bundle's
 *      OWN `/version.json` (ZVX-DEV-4423 — same-origin, same-atomic-swap as
 *      `index.html`; see {@link fetchWebVersionMarker}'s header for why
 *      `/health`, the API's cross-origin version, was the wrong thing to
 *      drive this decision from) on the SAME 5-minute/visibilitychange
 *      cadence as the SW update-check below. This can surface the prompt
 *      BEFORE the slow-network SW install even finishes downloading — which
 *      matters specifically for the operator's reported case (VPN-routed,
 *      slow but alive), where the SW-install signal alone could lag by
 *      minutes.
 *
 *   Either path dispatches the same {@link SW_UPDATE_AVAILABLE_EVENT} so
 *   `UpdateAvailableNotice` has one prompt, one place. Neither ever reloads
 *   on its own — an automatic reload mid-edit would lose unsaved work; the
 *   person always clicks "Update".
 *
 *   ZVX-DEV-3684 — the heartbeat path also dispatches
 *   {@link SW_UPDATE_RESOLVED_EVENT} when a later poll finds the mismatch is
 *   gone, so a notice that already prompted can self-dismiss instead of
 *   sitting there after the update has landed some other way.
 *
 *   ZVX-DEV-3717 — the fifth failure of this mechanism: the heartbeat was
 *   armed ONLY inside the 5-minute interval and the visibilitychange
 *   listener, so a person who loaded the page and simply stayed on it
 *   learned nothing for up to five minutes even when a newer build was
 *   already live (measured: zero polls in 45s on staging). {@link
 *   armHeartbeatPolling} now fires the same poll immediately once
 *   registration resolves, in addition to the interval and visibility
 *   triggers — see that function's header for why an immediate poll here
 *   does not race page startup.
 *
 *   ZVX-DEV-4423 — that same immediate poll is exactly what turned a failed
 *   update into a tight loop: click, reload, land back on a build that did
 *   not change, immediate poll re-detects "newer available", prompt returns
 *   instantly. Before dispatching, the heartbeat path now checks
 *   {@link isRepeatedFailedAttempt} against whatever
 *   `UpdateAvailableNotice.tsx` recorded just before its last reload
 *   (`readUpdateAttempt`, storage-backed so it survives the reload a
 *   module-level flag cannot). A genuine repeat dispatches
 *   {@link SW_UPDATE_STALE_EVENT} instead — told once, not re-asked — while
 *   a DIFFERENT server version (a newer deploy landed meanwhile) is treated
 *   as ordinary new information regardless of any prior attempt.
 */
export function registerSW(): void {
  // PROD-guard: never register SW in development mode.
  // Vite eliminates this entire block in dev builds (dead code elimination).
  if (!import.meta.env.PROD) return;

  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', {
        scope: '/',
        updateViaCache: 'none',
      })
      .then((registration) => {
        console.log('[SW] registered, scope:', registration.scope);

        registration.addEventListener('updatefound', () => {
          const installingWorker = registration.installing;
          if (!installingWorker) return;

          installingWorker.addEventListener('statechange', () => {
            const prompt = shouldPromptForUpdate({
              state: installingWorker.state,
              hasController: navigator.serviceWorker.controller !== null,
            });
            if (prompt) {
              window.dispatchEvent(
                new CustomEvent(SW_UPDATE_AVAILABLE_EVENT, { detail: { registration } }),
              );
            }
          });
        });

        // ZVX-DEV-3675 — the version-heartbeat fast-path. Compares
        // /version.json's { version } (ZVX-DEV-4423 — the web bundle's own
        // atomically-activated marker, not the API's /health) against this
        // build's own version; a strictly newer web build surfaces the same
        // prompt as the SW-install path, and also nudges registration.update()
        // so a real waiting worker (with a postMessage target) shows up as
        // soon as possible afterward.
        // ZVX-DEV-3684 — self-resolution. `heartbeatFired` already tracked
        // "already prompting" to avoid re-dispatching the same prompt every
        // poll; it now ALSO drives the reverse transition — if a later poll
        // finds the versions no longer mismatched (typically: the earlier
        // background revalidation finished and a later navigation picked up
        // the fresh cache, without the person ever clicking "Update"), tell
        // the notice to drop itself instead of sitting there stale. Reported
        // live: an operator's tab showed the new build on its own, minutes
        // after clicking, with the banner still up having done nothing.
        let heartbeatFired = false;
        let staleFired = false;
        // ZVX-DEV-6227 -- the last build this page was offered, so a NEWER one
        // is offered again even after the person closed the earlier prompt;
        // and the fact the idle-apply path reads on return from a long hide.
        let lastOfferedVersion: string | null = null;
        const pollHeartbeat = () => {
          fetchWebVersionWithFallback({
            fetchImpl: offlineSkippingFetch(fetch), // ZVX-DEV-8350: no request while the browser says offline
            // ZVX-DEV-6219 -- the api host's copy of the same marker, asked
            // only when the web origin's own answered 5xx or not at all.
            fallbackUrl: `${resolveApiBase().replace(/\/+$/, '')}/web-version.json`,
            // ZVX-DEV-6171 -- a 5xx from the web origin is reported through the api.
            onOriginTrouble: (trouble) =>
              void reportWebOriginTrouble(trouble, {
                apiBase: resolveApiBase(),
                version: appVersion || null,
              }),
          })
            .then(({ version: serverVersion, source }) => {
              const newer = isNewerVersionAvailable(appVersion || null, serverVersion);
              if (!newer) {
                // Nothing outstanding right now — whatever attempt was
                // recorded either succeeded or no longer applies; do not let
                // it suppress a genuinely new offer later.
                clearUpdateAttempt(window.localStorage);
                staleFired = false;
                if (heartbeatFired) {
                  heartbeatFired = false;
                  window.dispatchEvent(new CustomEvent(SW_UPDATE_RESOLVED_EVENT));
                }
                return;
              }
              registration.update().catch(() => {});
              const attempt = readUpdateAttempt(window.localStorage);
              if (
                isRepeatedFailedAttempt({
                  attempt,
                  currentVersion: appVersion || null,
                  serverVersion,
                })
              ) {
                if (staleFired) return; // already told once this session
                staleFired = true;
                window.dispatchEvent(
                  new CustomEvent(SW_UPDATE_STALE_EVENT, {
                    detail: { registration, serverVersion },
                  }),
                );
                return;
              }
              // ZVX-DEV-6227 -- quiet for the SAME build (no re-dispatch every
              // poll), loud again for a newer one: a dismissed "update to A"
              // must not swallow B.
              if (heartbeatFired && !shouldDispatchOffer({ lastOfferedVersion, serverVersion })) {
                return;
              }
              heartbeatFired = true;
              lastOfferedVersion = serverVersion ?? lastOfferedVersion;
              window.dispatchEvent(
                new CustomEvent(SW_UPDATE_AVAILABLE_EVENT, {
                  detail: {
                    registration,
                    serverVersion: serverVersion ?? undefined,
                    viaFallback: source === 'fallback',
                  },
                }),
              );
            })
            .catch(() => {});
        };

        // ZVX-DEV-3717 — one poll function, three triggers: immediately
        // (armHeartbeatPolling's fix for the fifth failure — see the file
        // header), every 5 minutes (was 60 — the SW itself always does
        // network-first for the shell, so this is belt-and-suspenders for
        // the update-available prompt specifically, and a 60-minute window
        // left staging operators on a stale tab for far too long relative to
        // how often deploys land), and the instant a backgrounded tab
        // becomes visible again (so "I switched back to this tab" feels
        // immediate rather than waiting for the next tick).
        armHeartbeatPolling({
          poll: () => {
            registration.update().catch(() => {});
            pollHeartbeat();
          },
          intervalMs: 5 * 60 * 1000,
          onVisible: (poll) => {
            // ZVX-DEV-6227 -- how long the tab was hidden decides whether the
            // return applies a known update quietly or merely polls.
            const hidden = createHiddenTracker();
            document.addEventListener('visibilitychange', () => {
              if (document.visibilityState === 'hidden') {
                hidden.onHidden();
                return;
              }
              if (document.visibilityState !== 'visible') return;
              const decision = decideIdleApply({
                hiddenForMs: hidden.onVisible(),
                newerBuildKnown: isNewerVersionAvailable(appVersion || null, lastOfferedVersion),
                activeEdit: hasActiveEdit(document),
              });
              if (decision.apply) {
                window.dispatchEvent(
                  new CustomEvent(SW_UPDATE_AUTO_APPLY_EVENT, {
                    detail: { registration, serverVersion: lastOfferedVersion ?? undefined },
                  }),
                );
                return;
              }
              poll();
            });
          },
        });
      })
      .catch((err) => {
        // Non-fatal: app works without SW (IDB is the data layer)
        console.warn('[SW] registration failed:', err);
      });
  });
}
